import React, { useMemo, useState, useEffect } from 'react';
import ApexCharts from 'react-apexcharts';
import { Form } from 'react-bootstrap';
import { fetchPickupHeatmapData, getGraphColor, rgbaToRgb, getDeltaMonthFormatedDate, getTodaysFormatedDate, rgbToRgba, rgbaToHex } from "./helpers";
import { CardFrame } from "components/UI/CardFrame";

import "assets/css/orders-heatmap.css"

const HeatmapComponent = () => {
    const [datasets, setPickupHeatmapData] = useState([]);
    const [selectedDataSet, setDataSet] = useState("ALL");
    const [startDate, setStartDate] = useState(getDeltaMonthFormatedDate(1)); // Set default state to today's date - 1 month
    const [endDate, setEndDate] = useState(getDeltaMonthFormatedDate(0)); // Set default state to today's date

    useEffect(() => {
        fetchPickupHeatmapData(setPickupHeatmapData, startDate, endDate);
    }, [startDate, endDate]);

    const buttonGroupComponent = useMemo(() => (
        <div className="row d-flex">
            <div className='col' style={{ width: 'auto' }}>
                Scheduled Pickup Density
            </div>
            <div className='col d-flex justify-content-end align-items-center'>
                <div className="form-group">
                    <label className="label-text">Filter Start:</label>
                    <Form.Control
                        defaultValue={startDate}
                        className='btn btn-sm tab-btn'
                        type='date'
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label className="label-text">Filter End:</label>
                    <Form.Control
                        value={endDate}
                        className='btn btn-sm tab-btn'
                        type='date'
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label className="label-text" htmlFor="dataset-select">Terminal:</label>
                    <Form.Select
                        defaultValue={selectedDataSet}
                        onChange={(e) => setDataSet(e.target.value)}
                        className="btn btn-sm tab-btn"
                        id="dataset-select"
                    >
                        {datasets && datasets.reverse().map((dataset, index) => (
                            <option className="btn btn-sm" key={index} value={dataset.name}>
                                {dataset.name}
                            </option>
                        ))}
                    </Form.Select>
                </div>
            </div>
        </div>
    ), [datasets]);

    const color = useMemo(() => rgbaToRgb(getGraphColor(selectedDataSet)), [selectedDataSet]);

    const calculateOverallTotal = (data) => {
        let overallTotal = 0;
        data.forEach(series => {
            series.data.forEach(point => {
                overallTotal += point.y;
            });
        });
        return overallTotal;
    };

    const calculateDailyTotals = (data) => {
        const totals = {};
        data.forEach(series => {
            series.data.forEach(point => {
                if (!totals[point.x]) {
                    totals[point.x] = 0;
                }
                totals[point.x] += point.y;
            });
        });
        return totals;
    };

    const calculateHighestHour = (data) => {
        let highestHour = 0;
        data.forEach(series => {
            series.data.forEach(point => {
                if (point.y > highestHour) {
                    highestHour = point.y;
                }
            });
        });
        return highestHour;
    };

    const calculateRanges = (highestNumber) => {
        let ranges = [];
        const colors = ['#104E8B', '#228B22', '#FF4500', '#8B0000'];
        const numDivisions = colors.length;
        const stepSize = highestNumber / numDivisions;
    
        for (let num = 0; num < numDivisions; num++) {
            const range = {
                from: Math.round(num * stepSize) + 1, // Start from 1 instead of 0
                to: Math.round((num + 1) * stepSize),
                color: colors[num],
            };
            ranges.push(range);
        }
    
        // Ensure the last range includes the highest number
        ranges[ranges.length - 1].to = highestNumber;
    
        return ranges;
    };
    
    


    return (
        <CardFrame title={buttonGroupComponent}>
            {datasets.map((dataset, index) => {
                if (dataset.name !== selectedDataSet) return null;

                const series = dataset.metrics.map(metric => ({
                    name: metric.name,
                    data: metric.data
                }));

                const dailyTotals = calculateDailyTotals(series);
                const overallTotal = calculateOverallTotal(series);
                const highestHourNumber = calculateHighestHour(series);
                const ranges = calculateRanges(highestHourNumber)

                return (
                    <div key={index}>
                        <ApexCharts
                            className="heatChart"
                            options={{
                                chart: { type: 'heatmap' },
                                plotOptions: {
                                    heatmap: {
                                        enableShades: true,
                                        shadeIntensity: 1,
                                        colorScale: {
                                            ranges: ranges
                                        },
                                        min: 1,
                                        max: highestHourNumber
                                    }
                                },
                                dataLabels: { enabled: true },
                                yaxis: {
                                    title: { text: 'Hour of the Day' },
                                    labels: {
                                        offsetY: 0,
                                        style: {
                                            fontSize: '12px', // Adjust font size if needed
                                        }
                                    }
                                },
                                annotations: {
                                    xaxis: [
                                        ...Object.keys(dailyTotals).map(date => ({
                                            x: date,
                                            borderColor: 'transparent', // Hide the border line
                                            label: {
                                                className: 'custom-annotation-label', // Add custom class
                                                style: {
                                                    color: '#FFFFFF', // Custom text color
                                                    background: color, // Custom background color
                                                    fontSize: '14px', // Custom font size
                                                    fontWeight: 'bold', // Custom font weight
                                                    padding: {
                                                        left: 30,
                                                        right: 30,
                                                        top: 3,
                                                        bottom: 3
                                                    },
                                                },
                                                text: `${dailyTotals[date]}`,
                                                position: 'bottom', // Position the label at the bottom
                                                orientation: 'horizontal', // Rotate the label to be horizontal
                                                offsetY: 55 // Adjust this value to move the label below the x-axis
                                            }
                                        })),
                                        {
                                            x: Object.keys(dailyTotals).length, // Position at the end
                                            borderColor: 'transparent', // Hide the border line
                                            label: {
                                                className: 'custom-annotation-label', // Add custom class
                                                style: {
                                                    color: '#FFFFFF', // Custom text color
                                                    background: color, // Custom background color
                                                    fontSize: '14px', // Custom font size
                                                    fontWeight: 'bold', // Custom font weight
                                                    padding: {
                                                        left: 30,
                                                        right: 30,
                                                        top: 3,
                                                        bottom: 3
                                                    },
                                                },
                                                text: `Total Orders Placed: ${overallTotal}`,
                                                position: 'top', // Position the label at the bottom
                                                orientation: 'horizontal', // Rotate the label to be horizontal
                                                offsetY: -40, // Adjust this value to move the label below the x-axis
                                                offsetX: 50
                                            }
                                        }
                                    ]
                                },
                            }}
                            series={series}
                            type="heatmap"
                            height={700}
                        />
                    </div>
                );
            })}
        </CardFrame>
    );
};

export default HeatmapComponent;
